import React, { useEffect, useRef, useState } from "react";
import { Icon } from "../shared/components";
import { ensureArray } from "../shared/utils";

const FREE_TEMPLATE = { id: "", name: "Mensagem livre (cliente já interagiu)", body_text: "" };

function formatPhone(raw) {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 0) return raw;
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}

export default function NewConversationModal({ api, user, users = [], onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [assignedTo, setAssignedTo] = useState(user?.id ?? "");
  const [templateId, setTemplateId] = useState("");
  const [message, setMessage] = useState("");
  const [templates, setTemplates] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const nameRef = useRef(null);

  useEffect(() => {
    nameRef.current?.focus();
    Promise.all([
      api.get("/whatsapp-numbers").catch(() => ({ data: [] })),
      api.get("/whatsapp-templates").catch(() => ({ data: [] })),
    ]).then(([numRes, tplRes]) => {
      setSessions(ensureArray(numRes.data));
      setTemplates(ensureArray(tplRes.data));
    });
    // eslint-disable-next-line
  }, []);

  // When template changes, fill in example body
  useEffect(() => {
    if (!templateId) {
      // keep current message if free
      return;
    }
    const tpl = templates.find((t) => String(t.id) === String(templateId));
    if (tpl?.body_text) setMessage(tpl.body_text);
  }, [templateId, templates]);

  const charCount = message.length;
  const selectedTpl = templates.find((t) => String(t.id) === String(templateId));

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const cleanPhone = phone.replace(/\D/g, "");
    if (!name.trim()) return setError("Informe o nome do cliente.");
    if (cleanPhone.length < 10) return setError("Informe um telefone válido com DDD.");
    if (!message.trim()) return setError("Informe uma mensagem.");

    setSending(true);
    try {
      // 1) Criar/encontrar contato
      const contactRes = await api.post("/contacts", {
        name: name.trim(),
        phone: cleanPhone,
      });
      const contact = contactRes.data;

      // 2) Criar conversa
      const convRes = await api.post("/conversations", {
        contact_id: contact.id,
        assigned_to: assignedTo || null,
        status: "atendimento",
        whatsapp_number: sessionId || "",
      });
      const conv = convRes.data;

      // 3) Enviar mensagem
      const finalMsg = message.replace(/\{nome\}/g, name.trim());
      if (selectedTpl && templateId) {
        await api.post(`/conversations/${conv.id}/send-template`, {
          template_id: templateId,
          variables: [name.trim()],
        }).catch(() =>
          api.post(`/conversations/${conv.id}/send-whatsapp`, { message: finalMsg })
        );
      } else {
        await api.post(`/conversations/${conv.id}/send-whatsapp`, { message: finalMsg });
      }

      onSuccess?.(conv);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Erro ao iniciar conversa.");
    } finally {
      setSending(false);
    }
  }

  // Close on Escape
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="newConvOverlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="newConvBox" role="dialog" aria-modal="true" aria-labelledby="newConvTitle">
        {/* Header */}
        <div className="newConvHeader">
          <div>
            <h3 id="newConvTitle">Iniciar nova conversa</h3>
            <p>Envie a primeira mensagem para um cliente novo via WhatsApp</p>
          </div>
          <button type="button" className="newConvClose" onClick={onClose} aria-label="Fechar">
            <Icon name="close" size={16} />
          </button>
        </div>

        {/* Body */}
        <form className="newConvBody" onSubmit={handleSubmit} noValidate>
          {/* Row 1: nome + telefone */}
          <div className="newConvGrid2">
            <div className="newConvField">
              <label htmlFor="ncv-name">Nome do cliente <span className="newConvReq">*</span></label>
              <input
                ref={nameRef}
                id="ncv-name"
                type="text"
                placeholder="Ex: João da Silva"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={sending}
                autoComplete="off"
              />
            </div>
            <div className="newConvField">
              <label htmlFor="ncv-phone">Telefone (com DDD) <span className="newConvReq">*</span></label>
              <div className="newConvPhoneWrap">
                <Icon name="whatsapp" size={15} style={{ color: "var(--brand-500)", flexShrink: 0 }} />
                <input
                  id="ncv-phone"
                  type="tel"
                  placeholder="(83) 99999-9999"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  disabled={sending}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          {/* Row 2: sessão + atribuir */}
          <div className="newConvGrid2">
            <div className="newConvField">
              <label htmlFor="ncv-session">Sessão WhatsApp</label>
              <select id="ncv-session" value={sessionId} onChange={(e) => setSessionId(e.target.value)} disabled={sending}>
                <option value="">Sessão padrão da empresa</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>{s.label} · {s.number}</option>
                ))}
              </select>
              <span className="newConvHint">Número que enviará a mensagem</span>
            </div>
            <div className="newConvField">
              <label htmlFor="ncv-assign">Atribuir a</label>
              <select id="ncv-assign" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} disabled={sending}>
                <option value="">Sem atribuição (fila)</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.id === user?.id ? `Eu (${u.name})` : u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 3: template */}
          <div className="newConvField">
            <label htmlFor="ncv-template">Template aprovado</label>
            <select id="ncv-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} disabled={sending}>
              <option value={FREE_TEMPLATE.id}>{FREE_TEMPLATE.name}</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <span className="newConvHint">Use um template para iniciar contato fora da janela de 24h</span>
          </div>

          {/* Row 4: mensagem */}
          <div className="newConvField">
            <label htmlFor="ncv-msg">Mensagem <span className="newConvReq">*</span></label>
            <textarea
              id="ncv-msg"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={sending || !!templateId}
              placeholder="Informe a mensagem aqui"
            />
            <span className="newConvHint">{charCount}/1024 caracteres · use {"{nome}"} para personalizar</span>
          </div>

          {/* Info banner */}
          <div className="newConvInfoBanner">
            <Icon name="info" size={15} style={{ color: "var(--info)", flexShrink: 0, marginTop: 1 }} />
            <div>
              <strong>Cliente novo será criado automaticamente</strong>
              <p>O contato aparecerá na lista de Contatos com o nome e telefone informados.</p>
            </div>
          </div>

          {error && <div className="newConvError"><Icon name="warning" size={14} /> {error}</div>}

          {/* Footer */}
          <div className="newConvFooter">
            <button type="button" className="newConvCancelBtn" onClick={onClose} disabled={sending}>
              Cancelar
            </button>
            <div className="newConvFooterRight">
              <button type="button" className="newConvScheduleBtn" disabled={sending} title="Em breve">
                <Icon name="clock" size={13} /> Agendar envio
              </button>
              <button type="submit" className="btn btn-primary" disabled={sending || !name.trim() || phone.replace(/\D/g, "").length < 10 || !message.trim()}>
                <Icon name="send" size={13} /> {sending ? "Iniciando..." : "Iniciar conversa"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
